import { Link } from 'react-router';

import { AuthenticatedRoute } from '@/app/constant';
import {
  NotificationCenterEmpty,
  NotificationCenterItem,
  NotificationCenterList,
} from '@/components/ui/notification-center';
import { Skeleton } from '@/components/ui/skeleton';
import { useJobStats } from '@/hooks/useJobs';
import { useMy } from '@/hooks/useMy';
import { useJobClassBreakdown } from '@/sections/topNavBar/NotificationCenter/useJobClassBreakdown';
import { JobStatus } from '@/types';

interface JobsActivityProps {
  /** Whether the Activity Hub popover is open — gates the running-jobs poll. */
  open: boolean;
  /** Closes the Activity Hub popover ("View all jobs" hands off to /jobs). */
  onClose: () => void;
}

/**
 * "Activity" section of the Activity Hub popover: aggregate job counts from
 * `useJobStats` (NEVER page-array lengths — the retired JobProgressBar
 * anti-pattern) plus a per-capability breakdown of currently running jobs.
 *
 * The two data sources have different freshness: the header counts come from
 * the hourly `#statistic#job#status#now` pre-aggregate, while the "Running
 * now" list is a live `status:JR` query. The header's "Updated hourly"
 * qualifier and the "Running now" section label keep the two from reading as
 * one contradictory live view (visual-QA finding C002).
 *
 * The running-jobs list only polls while the popover is open (`enabled: open`,
 * 10s interval); the cheap pre-aggregated stats query stays on its default
 * cadence.
 */
export function JobsActivity({ open, onClose }: JobsActivityProps) {
  const { jobStats, status: statsStatus } = useJobStats();
  const {
    data: runningJobs,
    status: runningStatus,
    isError: runningIsError,
    hasNextPage,
  } = useMy(
    { resource: 'job', query: 'status:JR' },
    { enabled: open, refetchInterval: 10_000 }
  );

  const breakdown = useJobClassBreakdown(runningJobs ?? []);

  const runningCount = jobStats?.[JobStatus.Running] ?? 0;
  const queuedCount = jobStats?.[JobStatus.Queued] ?? 0;

  return (
    <div>
      <div className="border-chariot-stroke flex items-start justify-between border-b px-4 py-2">
        <span className="text-chariot-text-primary text-sm font-semibold">
          Activity
        </span>
        {statsStatus === 'success' && (
          <div className="text-right">
            <p className="text-chariot-text-secondary text-xs tabular-nums">
              {runningCount.toLocaleString()} running ·{' '}
              {queuedCount.toLocaleString()} queued
            </p>
            {/* Copy hard-codes the hourly cron cadence of the
                `#statistic#job#status#now` aggregate these counts come from. */}
            <p className="text-chariot-text-secondary text-xs">
              Updated hourly
            </p>
          </div>
        )}
      </div>
      <p className="text-chariot-text-secondary px-4 pt-3 pb-1 text-xs font-semibold tracking-wider uppercase">
        Running now
      </p>
      {runningStatus === 'pending' ? (
        <div aria-busy="true">
          <span className="sr-only">Loading running jobs</span>
          {[0, 1, 2].map(i => (
            <NotificationCenterItem key={i} aria-hidden="true">
              <div className="min-w-0 flex-1">
                <Skeleton shape="line" className="w-1/2" />
                <Skeleton shape="line" className="mt-2 h-2 w-1/3" />
              </div>
              <Skeleton shape="line" className="w-6" />
            </NotificationCenterItem>
          ))}
        </div>
      ) : runningIsError ? (
        <NotificationCenterEmpty>
          <span className="text-chariot-text-primary text-sm font-medium">
            Couldn&apos;t load running jobs
          </span>
          <span className="text-chariot-text-secondary text-xs">
            Retrying automatically.
          </span>
        </NotificationCenterEmpty>
      ) : breakdown.length === 0 ? (
        <NotificationCenterEmpty>
          <span>No jobs running right now</span>
          {statsStatus === 'success' && runningCount > 0 && (
            <span className="text-chariot-text-secondary text-center text-xs">
              The totals above update hourly and may lag live activity.
            </span>
          )}
        </NotificationCenterEmpty>
      ) : (
        <>
          <NotificationCenterList>
            {breakdown.map(row => (
              <NotificationCenterItem key={row.id}>
                <div className="min-w-0 flex-1">
                  <p className="text-chariot-text-primary truncate text-sm font-medium">
                    {row.displayName}
                  </p>
                  <p className="text-chariot-text-secondary text-xs">
                    {row.isIntegration ? 'Integration' : 'Capability'} ·{' '}
                    {row.executor}
                  </p>
                </div>
                <span className="text-chariot-text-secondary text-xs font-semibold tabular-nums">
                  {row.count}
                </span>
              </NotificationCenterItem>
            ))}
          </NotificationCenterList>
          {/* The running-jobs query reads only the first /my page (the popover
              never paginates), so on very large tenants the breakdown can be
              partial — say so rather than presenting it as exhaustive. */}
          {hasNextPage && (
            <p className="text-chariot-text-secondary px-4 pb-2 text-xs">
              Showing the first page of running jobs — view all jobs for the
              full list.
            </p>
          )}
        </>
      )}
      <div className="border-chariot-stroke border-t px-4 py-0.5">
        <Link
          to={AuthenticatedRoute.JOBS}
          relative="path"
          onClick={onClose}
          className="text-chariot-link inline-block py-1.5 text-sm font-medium transition-opacity hover:opacity-70"
        >
          View all jobs
        </Link>
      </div>
    </div>
  );
}
